import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../configs/axiosInstance";

const apiUrl = import.meta.env.VITE_APP_API;

const Checkout = () => {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderLoading, setOrderLoading] = useState(false);
    const [paymentRedirecting, setPaymentRedirecting] = useState(false);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
    const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
    const [promoCode, setPromoCode] = useState("");
    const [deliveryMethod, setDeliveryMethod] = useState("outside_dhaka");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [districts, setDistricts] = useState([]);
    const [areas, setAreas] = useState([]);
    const [districtsLoading, setDistrictsLoading] = useState(false);
    const [areasLoading, setAreasLoading] = useState(false);
    const [formData, setFormData] = useState({
        district: "",
        districtId: "",
        area: "",
        areaId: "",
        address: "",
        mobile: "",
        name: "",
        postalCode: "",
        notes: ""
    });

    useEffect(() => {
        loadCartItems();
        loadPaymentMethods();
        loadDistricts();
    }, []);

    useEffect(() => {
        if (formData.districtId) {
            loadAreas(formData.districtId);
        } else {
            setAreas([]);
            setFormData(prev => ({ ...prev, area: "", areaId: "" }));
        }
    }, [formData.districtId]);

    const loadDistricts = async () => {
        setDistrictsLoading(true);
        try {
            const response = await fetch(`${apiUrl}/api/bookstore/locations/districts`);
            const result = await response.json();
            if (result.data) {
                setDistricts(result.data);
            }
        } catch (error) {
            console.error('Error loading districts:', error);
            toast.error('Failed to load districts');
        } finally {
            setDistrictsLoading(false);
        }
    };

    const loadAreas = async (districtId) => {
        setAreasLoading(true);
        try {
            const response = await fetch(`${apiUrl}/api/bookstore/locations/areas/?district_id=${districtId}`);
            const result = await response.json();
            if (result.data) {
                setAreas(result.data);
            }
        } catch (error) {
            console.error('Error loading areas:', error);
            toast.error('Failed to load areas');
            setAreas([]);
        } finally {
            setAreasLoading(false);
        }
    };

    const loadPaymentMethods = async () => {
        setPaymentMethodsLoading(true);
        try {
            const response = await axios.get('/api/payment/available_payment_methods');
            setAvailablePaymentMethods(response.data);
            
            // Set first payment method as default if available
            if (response.data && response.data.length > 0) {
                setPaymentMethod(response.data[0].name);
            }
        } catch (error) {
            console.error('Error loading payment methods:', error);
            toast.error('Failed to load payment methods');
            
            // Fallback to default payment methods if API fails
            const fallbackMethods = [
                { display_name: "bKash", name: "bkash", icon: null },
                { display_name: "Nagad", name: "nagad", icon: null },
                { display_name: "Rocket", name: "rocket", icon: null }
            ];
            setAvailablePaymentMethods(fallbackMethods);
            setPaymentMethod("bkash");
        } finally {
            setPaymentMethodsLoading(false);
        }
    };

    const loadCartItems = () => {
        try {
            const storedCart = localStorage.getItem('cartItems');
            if (storedCart) {
                const items = JSON.parse(storedCart);
                setCartItems(items);
            } else {
                navigate('/cart');
                return;
            }
        } catch (error) {
            console.error('Error loading cart items:', error);
            navigate('/cart');
        } finally {
            setLoading(false);
        }
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((total, item) => {
            const price = item.discount > 0 ? item.price - item.discount : item.price;
            return total + (price * item.quantity);
        }, 0);
    };

    const getShippingCharge = () => {
        switch (deliveryMethod) {
            case "courier_office":
                return 0;
            case "inside_dhaka":
                return 70;
            case "outside_dhaka":
                return 130;
            default:
                return 130;
        }
    };

    const calculateTotal = () => {
        return calculateSubtotal() + getShippingCharge();
    };

    const getPaymentMethodColor = (methodName) => {
        const colorMap = {
            'bkash': 'bg-red-600',
            'nagad': 'bg-orange-500',
            'rocket': 'bg-purple-600',
            'upay': 'bg-green-600',
            'sure_cash': 'bg-blue-600',
            'mcash': 'bg-indigo-600',
            'ok_wallet': 'bg-yellow-600',
            'tap': 'bg-pink-600'
        };
        return colorMap[methodName.toLowerCase()] || 'bg-gray-600';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'district') {
            const selectedDistrict = districts.find(district => district.cityId === parseInt(value));
            setFormData(prev => ({
                ...prev,
                district: selectedDistrict ? selectedDistrict.cityBangla : '',
                districtId: value,
                area: '',
                areaId: ''
            }));
        } else if (name === 'area') {
            const selectedArea = areas.find(area => area.areaId === parseInt(value));
            setFormData(prev => ({
                ...prev,
                area: selectedArea ? selectedArea.areaBangla : '',
                areaId: value
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleApplyPromoCode = () => {
        if (!promoCode.trim()) {
            toast.error('Please enter a promo code');
            return;
        }
        toast.success('Promo code applied successfully!');
    };

    const handleConfirmOrder = async () => {
        if (!formData.district || !formData.area || !formData.address || !formData.mobile || !formData.name) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (formData.mobile.length < 11) {
            toast.error('Please enter a valid mobile number');
            return;
        }

        if (!paymentMethod) {
            toast.error('Please select a payment method');
            return;
        }

        setOrderLoading(true);

        try {
            // Map delivery method to API format
            const deliveryMethodMap = {
                "courier_office": "COURIER_OFFICE",
                "inside_dhaka": "HOME_DELIVERY",
                "outside_dhaka": "HOME_DELIVERY"
            };

            // Use payment method name directly from API
            const paymentMethodName = paymentMethod.toUpperCase();

            // Prepare order data according to API schema
            const orderData = {
                items: cartItems.map(item => ({
                    book_id: item.id,
                    quantity: item.quantity,
                    price: item.discount > 0 ? item.price - item.discount : item.price
                })),
                delivery_method: deliveryMethodMap[deliveryMethod] || "HOME_DELIVERY",
                payment_method: paymentMethodName,
                promo_code: promoCode || null,
                subtotal: calculateSubtotal(),
                shipping_charge: getShippingCharge(),
                total: calculateTotal(),
                delivery_name: formData.name,
                delivery_phone: formData.mobile,
                delivery_address: `${formData.address}, ${formData.area}`,
                delivery_city: formData.district,
                delivery_postal_code: formData.postalCode || "",
                notes: formData.notes || ""
            };

            console.log('Order Data:', orderData);

            // Make API call to create order
            const response = await axios.post('/api/bookstore/orders/', orderData);
            
            console.log('Order Response:', response.data);
            
            const orderResponse = response.data;
            
            // Clear cart first
            localStorage.removeItem('cartItems');
            localStorage.setItem('cartCount', '0');
            if (window.updateCartCount) {
                window.updateCartCount(0);
            }
            
            // Check if payment URL exists and payment is required
            if (orderResponse.payment_url ) {
                    window.location.href = orderResponse.payment_url;
                return;
            } else if (paymentMethodName === 'COD') {
                toast.success('Order placed successfully! Cash on Delivery selected.');
                
                // Redirect to book store for COD orders
                navigate('/book-store');
            } else {
                toast.success('Order placed successfully!');
                
                // Redirect to book store if no payment URL
                navigate('/book-store');
            }
        } catch (error) {
            console.error('Order placement failed:', error);
            
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Failed to place order. Please try again.');
            }
        } finally {
            setOrderLoading(false);
            setPaymentRedirecting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading checkout...</p>
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <Link
                        to="/cart"
                        className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Cart
                    </Link>
                    <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
                    <div></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Order Summary</h2>
                            <div className="space-y-4">
                                {cartItems.map((item, index) => (
                                    <div key={item.id} className="flex justify-between items-center">
                                        <div className="flex-1">
                                            <p className="text-gray-900 font-medium">{index + 1}. {item.name}</p>
                                            <p className="text-sm text-gray-500">{item.quantity} x Tk. {item.discount > 0 ? (item.price - item.discount).toFixed(0) : item.price.toFixed(0)}</p>
                                        </div>
                                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                            Tk. {item.discount > 0 ? (item.price - item.discount).toFixed(0) : item.price.toFixed(0)}
                                        </span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                    <span className="text-gray-600 font-medium">Shipping Charge</span>
                                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        Tk. {getShippingCharge()}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                    <span className="text-lg font-semibold text-gray-900">Total</span>
                                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        Tk. {calculateTotal()}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex space-x-3">
                                    <input
                                        type="text"
                                        placeholder="Apply promo code"
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value)}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                    />
                                    <button
                                        onClick={handleApplyPromoCode}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Method</h2>
                            <div className="space-y-4">
                                {[
                                    { value: "courier_office", label: "কুরিয়ার অফিস থেকে সংগ্রহ করব (সারাদেশ, ০ ৳)" },
                                    { value: "inside_dhaka", label: "Home Delivery (Inside Dhaka, 70 ৳)" },
                                    { value: "outside_dhaka", label: "Home Delivery (Outside Dhaka, 130 ৳)" }
                                ].map((option) => (
                                    <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="deliveryMethod"
                                            value={option.value}
                                            checked={deliveryMethod === option.value}
                                            onChange={(e) => setDeliveryMethod(e.target.value)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                        />
                                        <span className="text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Address</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">আপনার নাম* <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">জেলা* <span className="text-red-500">*</span></label>
                                    {districtsLoading ? (
                                        <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                                            <div className="animate-pulse flex space-x-4">
                                                <div className="flex-1 space-y-2 py-1">
                                                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <select
                                            name="district"
                                            value={formData.districtId}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                            required
                                        >
                                            <option value="">জেলা নির্বাচন করুন</option>
                                            {districts.map((district) => (
                                                <option key={district.cityId} value={district.cityId}>
                                                    {district.cityBangla}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">অঞ্চল* <span className="text-red-500">*</span></label>
                                    {areasLoading ? (
                                        <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                                            <div className="animate-pulse flex space-x-4">
                                                <div className="flex-1 space-y-2 py-1">
                                                    <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <select
                                            name="area"
                                            value={formData.areaId}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                            required
                                            disabled={!formData.districtId}
                                        >
                                            <option value="">
                                                {!formData.districtId ? 'প্রথমে জেলা নির্বাচন করুন' : 'অঞ্চল নির্বাচন করুন'}
                                            </option>
                                            {areas.map((area) => (
                                                <option key={area.areaId} value={area.areaId}>
                                                    {area.areaBangla}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">বাসার ঠিকানা (বিস্তারিত)* <span className="text-red-500">*</span></label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your detailed address"
                                        required
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">তোমার মোবাইল নাম্বার* <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your mobile number"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">পোস্টাল কোড</label>
                                    <input
                                        type="text"
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter postal code (optional)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">বিশেষ নোট</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="2"
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Any special instructions (optional)"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Payment Details</h2>
                            <p className="text-gray-600 mb-4">Select Any Payment Method</p>
                            
                            {paymentMethodsLoading ? (
                                <div className="space-y-4 mb-6">
                                    {[1, 2, 3].map((index) => (
                                        <div key={index} className="flex items-center space-x-3">
                                            <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                                            <div className="bg-gray-200 rounded-full px-3 py-2 w-32 h-8 animate-pulse"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-4 mb-6">
                                    {availablePaymentMethods.map((method) => (
                                        <label key={method.name} className="flex items-center space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value={method.name}
                                                checked={paymentMethod === method.name}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <div className="flex items-center space-x-2">
                                                {method.icon && (
                                                    <img 
                                                        src={method.icon} 
                                                        alt={method.display_name}
                                                        className="w-6 h-6 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <div className={`${getPaymentMethodColor(method.name)} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                                                    {method.display_name}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                            
                            <button
                                onClick={handleConfirmOrder}
                                disabled={orderLoading || paymentMethodsLoading || paymentRedirecting}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center"
                            >
                                {paymentRedirecting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                                        Redirecting to Payment...
                                    </>
                                ) : orderLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                                        Processing Order...
                                    </>
                                ) : (
                                    'Confirm Order'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;